import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, of, Subject, Subscription, throwError, timer } from "rxjs";
import {
  map,
  catchError,
  takeUntil,
  take,
  tap,
  switchMap,
} from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ActivatedRoute, Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  serverUrl = environment.baseUrl;
  errorData: {};
  endTime = 20;
  unsubscribe$: Subject<void> = new Subject();
  timerSubscription: Subscription;
  minutesDisplay = 0;
  secondsDisplay = 0;
  currentUser: any;
  private refreshSub: Subscription;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.currentUser = JSON.parse(localStorage.getItem("currentUser") || "[]");
  }

  redirectUrl: string;

  login(username: string, password: string): Observable<any> {
    return this.http
      .post<any>(`${this.serverUrl}/api/login`, { username, password })
      .pipe(
        tap((response) => {
          if (response?.token && response?.refresh_token) {
            const decodedToken: any = jwtDecode(response.token);

            const userData = {
              token: response.token,
              refresh_token: response.refresh_token,
              user: {
                id: decodedToken.id,
                username: decodedToken.username,
                display_name: decodedToken.display_name,
                user_email: decodedToken.user_email,
              },
            };

            localStorage.setItem("currentUser", JSON.stringify(userData));

            if (response?.token && response?.refresh_token) {
              this.startTokenAutoRefresh();
            }
          }
        }),
      );
  }

  getDecodedToken(): any | null {
    const currentUserStr = localStorage.getItem("currentUser");
    if (!currentUserStr) return null;

    try {
      const currentUser = JSON.parse(currentUserStr);
      const token = currentUser?.token;
      if (!token) return null;

      return jwtDecode(token);
    } catch (e) {
      return null;
    }
  }

  startTokenAutoRefresh() {
    const decoded = this.getDecodedToken();
    if (!decoded?.exp) return;

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = decoded.exp - now;
    const refreshTime = (expiresIn - 60) * 1000; // 1 minute before expiry

    if (this.refreshSub) this.refreshSub.unsubscribe();

    if (refreshTime <= 0) {
      this.refreshAccessToken().subscribe({
        next: () => this.startTokenAutoRefresh(),
        error: () => this.logout(),
      });
      return;
    }

    this.refreshSub = timer(refreshTime)
      .pipe(switchMap(() => this.refreshAccessToken()))
      .subscribe({
        next: () => this.startTokenAutoRefresh(),
        error: () => this.logout(),
      });
  }

  refreshAccessToken(): Observable<string> {
    const refresh_token = this.getRefreshToken();

    if (!refresh_token) {
      this.logout();
      return throwError(() => new Error("Missing refresh token"));
    }

    return this.http
      .post<any>(`${this.serverUrl}/api/refresh_token`, {
        refresh_token: refresh_token,
      })
      .pipe(
        switchMap((res) => {
          if (res?.access_token) {
            const currentUserStr = localStorage.getItem("currentUser");
            if (!currentUserStr) {
              this.logout();
              return throwError(() => new Error("No current user"));
            }

            const currentUser = JSON.parse(currentUserStr);
            currentUser.token = res.access_token;

            localStorage.setItem("currentUser", JSON.stringify(currentUser));

            return of(res.access_token);
          } else {
            return throwError(
              () => new Error("Invalid refresh token response"),
            );
          }
        }),
        catchError((err) => {
          this.logout();
          return throwError(() => err);
        }),
      );
  }

  getRefreshToken(): string | null {
    const currentUserStr = localStorage.getItem("currentUser");
    if (!currentUserStr) return null;

    const currentUser = JSON.parse(currentUserStr);
    return currentUser?.refresh_token || null;
  }

  getToken(): string | null {
    return localStorage.getItem("token");
  }

  check_account(token: string) {
    const userId = this.currentUser.user_id;
    return this.http.get<any>(
      this.serverUrl + "/api/check_account/" + userId + "/" + token,
    );
  }

  isLoggedIn(): boolean {
    const currentUserStr = localStorage.getItem("currentUser");
    if (!currentUserStr) return false;

    const currentUser = JSON.parse(currentUserStr);
    const token = currentUser?.token;
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      const now = Math.floor(new Date().getTime() / 1000);
      return decoded.exp > now;
    } catch (err) {
      return false;
    }
  }

  // A valid refresh token means we can silently obtain a new access token
  // instead of forcing the user back to the login screen.
  isRefreshTokenValid(): boolean {
    const refresh_token = this.getRefreshToken();
    if (!refresh_token) return false;

    try {
      const decoded: any = jwtDecode(refresh_token);
      if (!decoded?.exp) return true;
      const now = Math.floor(new Date().getTime() / 1000);
      return decoded.exp > now;
    } catch (err) {
      return false;
    }
  }

  getAuthorizationToken() {
    const currentUserStr = localStorage.getItem("currentUser");
    if (!currentUserStr) return null;

    const currentUser = JSON.parse(currentUserStr);
    return currentUser?.token;
  }

  logout() {
    if (this.refreshSub) this.refreshSub.unsubscribe(); // stop timer
    localStorage.removeItem("currentUser");
    this.router.navigate(["/login"]);
  }

  recover_password(user_email: string) {
    return this.http
      .post<any>(`${this.serverUrl}/api/forgot_password`, {
        user_email: user_email,
      })
      .pipe(
        map((user) => {
          if (user && user.status === "success") {
            localStorage.setItem("currentUser", JSON.stringify(user));
            return user;
          }
        }),
        catchError(this.handleError),
      );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error("An error occurred:", error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`,
      );
    }

    // return an observable with a user-facing error message
    this.errorData = {
      errorTitle: "Oops! Request for document failed",
      errorDesc: "Something bad happened. Please try again later.",
    };
    return throwError(this.errorData);
  }
}
